import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  FilterPresetList,
  FilterPresetNameModal,
  FilterPresetSaveButton,
} from '../../../shared/filter-presets/filter-presets.jsx';
import EntityLink from '../../../shared/ui/entity-link/entity-link.jsx';
import FloatingAlerts from '../../../shared/ui/floating-alerts/floating-alerts.jsx';
import LoadingContainer from '../../../shared/ui/loading-container/loading-container.jsx';

const ACCOUNT_TYPE_OPTIONS = [
  { value: 'demo', labelKey: 'accounts.typeDemo' },
  { value: 'live', labelKey: 'accounts.typeLive' },
  { value: 'wallet', labelKey: 'accounts.typeWallet' },
];

function getAriaSort(sort, field) {
  if (sort?.sortBy !== field) {
    return 'none';
  }

  return sort?.sortOrder === 'asc' ? 'ascending' : 'descending';
}

function SortableHeader({ field, label, sort, onSortChange }) {
  const isActive = sort?.sortBy === field;
  const iconClass = isActive
    ? (sort?.sortOrder === 'asc' ? 'ti-arrow-up' : 'ti-arrow-down')
    : 'ti-arrows-sort';

  return (
    <button
      type="button"
      className={`btn btn-link p-0 text-decoration-none fw-semibold ${isActive ? 'text-primary' : 'text-muted'}`}
      onClick={() => onSortChange(field)}
    >
      <span>{label}</span>
      <i className={`ti ${iconClass} ms-1`} aria-hidden="true"></i>
    </button>
  );
}

function PendingVerificationBadge({ label }) {
  return (
    <span className="badge rounded-pill bg-warning-subtle text-warning border border-warning-subtle">
      {label}
    </span>
  );
}

function SyncFreshnessIndicator({ accountId, status, title }) {
  const className = {
    fresh: 'bg-success',
    warning: 'bg-warning',
    stale: 'bg-danger',
  }[status] || 'bg-danger';

  return (
    <span
      data-testid={`account-sync-freshness-${accountId}`}
      className={`d-inline-block rounded-circle me-2 ${className}`}
      style={{ width: '0.65rem', height: '0.65rem' }}
      title={title || 'Last Sync: -'}
      aria-label={title || 'Last Sync: -'}
    ></span>
  );
}

function AccountsPageView({
  rows,
  isLoading,
  isLoadingMore,
  hasMoreAccounts,
  syncingId,
  error,
  success,
  sort,
  filters,
  isFilterOpen,
  changeSort,
  updateFilter,
  applyFilters,
  clearFilters,
  setIsFilterOpen,
  syncAccount,
  loadMoreAccounts,
  filterPresets,
  isFilterPresetModalOpen,
  filterPresetName,
  filterPresetNameError,
  openSaveFilterPreset,
  closeSaveFilterPreset,
  setFilterPresetName,
  saveFilterPreset,
  applyFilterPreset,
  deleteFilterPreset,
}) {
  const navigate = useNavigate();
  const { t } = useTranslation();

  return (
    <div>
      <FloatingAlerts error={error} success={success} />
      <div className="app-page-header d-flex align-items-start justify-content-between gap-3">
        <div>
          <h2>{t('accounts.title')}</h2>
          <p>{t('accounts.subtitle')}</p>
        </div>
        <button
          type="button"
          className="btn btn-outline-secondary d-flex align-items-center"
          onClick={() => setIsFilterOpen((prev) => !prev)}
        >
          <i className="ti ti-adjustments-horizontal me-1 fs-5" aria-hidden="true"></i>
          {t('accounts.filters')}
        </button>
      </div>

      <div className="widget-content searchable-container list">
        {isFilterOpen ? (
          <form
            className="card card-body"
            onSubmit={(event) => {
              event.preventDefault();
              applyFilters();
            }}
          >
            <div className="row g-2 align-items-end">
              <div className="col-12 col-md-6 col-xl-2">
                <label className="form-label" htmlFor="accounts-filter-login">{t('accounts.filterLogin')}</label>
                <input
                  id="accounts-filter-login"
                  name="login"
                  type="search"
                  className="form-control"
                  value={filters?.login || ''}
                  onChange={(event) => updateFilter('login', event.target.value)}
                />
              </div>
              <div className="col-12 col-md-6 col-xl-2">
                <label className="form-label" htmlFor="accounts-filter-type">{t('accounts.filterType')}</label>
                <select
                  id="accounts-filter-type"
                  name="type"
                  className="form-select"
                  value={filters?.type || ''}
                  onChange={(event) => updateFilter('type', event.target.value)}
                >
                  <option value="">{t('accounts.allTypes')}</option>
                  {ACCOUNT_TYPE_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>{t(option.labelKey)}</option>
                  ))}
                </select>
              </div>
              <div className="col-12 col-md-6 col-xl-2">
                <label className="form-label" htmlFor="accounts-filter-platform">{t('accounts.filterPlatform')}</label>
                <input
                  id="accounts-filter-platform"
                  name="platform"
                  type="search"
                  className="form-control"
                  value={filters?.platform || ''}
                  onChange={(event) => updateFilter('platform', event.target.value)}
                />
              </div>
              <div className="col-12 col-md-6 col-xl-2">
                <label className="form-label" htmlFor="accounts-filter-currency">{t('accounts.filterCurrency')}</label>
                <input
                  id="accounts-filter-currency"
                  name="currency"
                  type="search"
                  className="form-control text-uppercase"
                  value={filters?.currency || ''}
                  onChange={(event) => updateFilter('currency', event.target.value)}
                />
              </div>
              <div className="col-12 col-md-6 col-xl-2">
                <label className="form-label" htmlFor="accounts-filter-server">{t('accounts.filterServer')}</label>
                <input
                  id="accounts-filter-server"
                  name="server"
                  type="search"
                  className="form-control"
                  value={filters?.server || ''}
                  onChange={(event) => updateFilter('server', event.target.value)}
                />
              </div>
              <div className="col-12 col-md-6 col-xl-2">
                <div className="d-flex gap-2">
                  <button type="submit" className="btn btn-primary flex-fill">
                    <i className="ti ti-filter me-1" aria-hidden="true"></i>
                    {t('accounts.applyFilters')}
                  </button>
                  <FilterPresetSaveButton onClick={openSaveFilterPreset} />
                  <button type="button" className="btn btn-outline-secondary" onClick={clearFilters}>
                    <i className="ti ti-x me-1" aria-hidden="true"></i>
                    {t('accounts.clearFilters')}
                  </button>
                </div>
              </div>
            </div>
            <FilterPresetList presets={filterPresets} onApply={applyFilterPreset} onDelete={deleteFilterPreset} />
          </form>
        ) : null}
        <div className={`card card-body${isFilterOpen ? ' mt-3' : ''}`}>
          <LoadingContainer isLoading={isLoading} hasData={rows.length > 0}>
            <div className="table-responsive">
              <table className="table search-table align-middle text-nowrap mb-0">
              <thead className="header-item">
                <tr>
                  <th aria-sort={getAriaSort(sort, 'login')}>
                    <SortableHeader field="login" label={t('accounts.login')} sort={sort} onSortChange={changeSort} />
                  </th>
                  <th>{t('common.type')}</th>
                  <th>{t('accounts.platform')}</th>
                  <th>{t('accounts.currency')}</th>
                  <th>{t('accounts.balance')}</th>
                  <th>{t('accounts.equity')}</th>
                  <th>{t('accounts.freeMargin')}</th>
                  <th>{t('accounts.usedMargin')}</th>
                  <th aria-sort={getAriaSort(sort, 'leverage')}>
                    <SortableHeader field="leverage" label={t('accounts.leverage')} sort={sort} onSortChange={changeSort} />
                  </th>
                  <th>{t('accounts.profile')}</th>
                  <th aria-sort={getAriaSort(sort, 'created')}>
                    <SortableHeader field="created" label={t('common.date')} sort={sort} onSortChange={changeSort} />
                  </th>
                  <th>{t('common.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {!isLoading && rows.length === 0 ? (
                  <tr>
                    <td colSpan="12" className="text-center text-muted">{t('accounts.noAccountsFound')}</td>
                  </tr>
                ) : null}

                {rows.map((row) => {
                  const profilePath = row.profileUid ? `/profiles/${encodeURIComponent(row.profileUid)}` : '';
                  const isSyncDisabled = Boolean(syncingId) || Boolean(row.isPendingVerification);

                  return (
                    <tr key={row.key} className="app-clickable-row" onClick={() => navigate('/accounts/' + row.accountId)}>
                      <td>
                        <SyncFreshnessIndicator
                          accountId={row.accountId}
                          status={row.lastSyncStatus}
                          title={row.lastSyncTitle}
                        />
                        {row.isPendingVerification ? (
                          <PendingVerificationBadge label={t('accounts.pendingVerification')} />
                        ) : (
                          <>{row.login}<small className="d-block text-body-secondary">{t('accounts.accountNumber', { defaultValue: 'Account number' })}: {row.accountNumber ?? row.source?.accountNumber ?? row.source?.login ?? '-'}</small></>
                        )}
                      </td>
                      <td>{row.type}</td>
                      <td>{row.platform}</td>
                      <td>{row.currency}</td>
                      <td>{row.balance}</td>
                      <td>{row.equity}</td>
                      <td>{row.freeMargin}</td>
                      <td>{row.usedMargin}</td>
                      <td>{row.leverage}</td>
                      <td
                        className={profilePath ? 'app-clickable-row' : undefined}
                        onClick={(event) => {
                          if (!profilePath) {
                            return;
                          }

                          event.stopPropagation();
                          navigate(profilePath);
                        }}
                      >
                        {profilePath ? (
                          <EntityLink
                            to={profilePath}
                            onClick={(event) => event.stopPropagation()}
                          >
                            {row.profileName}
                          </EntityLink>
                        ) : (
                          row.profileName
                        )}
                      </td>
                      <td>{row.createdAt}</td>
                      <td onClick={(event) => event.stopPropagation()}>
                        <div className="btn-group btn-group-sm" role="group">
                          <button
                            type="button"
                            className="btn btn-outline-primary"
                            onClick={() => syncAccount(row)}
                            disabled={isSyncDisabled}
                            title={row.isPendingVerification ? t('accounts.pendingVerificationSyncDisabled') : undefined}
                          >
                            {syncingId && String(syncingId) === String(row.accountId) ? t('accounts.syncing') : t('accounts.sync')}
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              </table>
            </div>
          </LoadingContainer>
          {hasMoreAccounts && !isLoading ? (
            <div className="text-center mt-3">
              <button
                type="button"
                className="btn btn-outline-primary"
                onClick={loadMoreAccounts}
                disabled={isLoadingMore}
              >
                {isLoadingMore ? t('common.loading') : t('accounts.loadMore')}
              </button>
            </div>
          ) : null}
        </div>
      </div>
      <FilterPresetNameModal
        isOpen={isFilterPresetModalOpen}
        name={filterPresetName}
        error={filterPresetNameError}
        onNameChange={setFilterPresetName}
        onSubmit={saveFilterPreset}
        onClose={closeSaveFilterPreset}
      />
    </div>
  );
}

export default AccountsPageView;
